import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react'
import {
	GetAssessmentQuery,
	useGetApplicantsLazyQuery,
	useGetAssessmentLazyQuery,
	GetApplicantsQuery,
	EnumSortApplicantGetManyInput,
	CoreApplicantItemFieldsFragment
} from 'hooks/useApollo'
import debounce from 'lodash.debounce'
import { useLocation, useNavigate } from 'react-router'
import { Option, useSearchFilter } from 'hooks/useFilter'
import useIntl from 'hooks/useIntl'
import { removeUndefinedKeys } from 'utils/object'
import { useApolloClient } from '@apollo/client'
import { GET_APPLICANTS } from 'constants/queries/applicant'

export interface TeamContextProps {
	debounceSearch: string
	filters: ReturnType<typeof useSearchFilter>
	page: number
	talents: CoreApplicantItemFieldsFragment[]
	allApplicants: CoreApplicantItemFieldsFragment[]
	demo: boolean
	teamId: string
	handleSearch: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
	handlePageChange: (e: React.ChangeEvent<unknown> | undefined, value: number) => void
	assessment: GetAssessmentQuery['assessment'] | undefined
	applicants: GetApplicantsQuery['applicants']
	getAssessment: ReturnType<typeof useGetAssessmentLazyQuery>[0]
	getApplicants: ReturnType<typeof useGetApplicantsLazyQuery>[0]
	assessmentLoading: boolean
	applicantsLoading: boolean
	refetchAssessment(): Promise<void>
	refetchApplicants(
		type?: 'Internal' | 'External' | null
	): Promise<TeamContextProps['applicants'] | undefined>
	order: 'ASC' | 'DESC'
	sort: EnumSortApplicantGetManyInput
	handleToggleOrder: () => void
	handleChangeSort: (event: React.ChangeEvent) => void
	fetchAllApplicants(): Promise<CoreApplicantItemFieldsFragment[]>
	resetExportMode(): void
}

// @ts-ignore
export const TeamsContext = createContext<TeamContextProps>()

export function useTeamContext<T>(selector: (store: TeamContextProps) => T) {
	const context = useContext(TeamsContext)
	if (context === undefined) {
		throw new Error('useTeamContext must be used within a TeamsProvider')
	}
	return selector(context)
}

export const TeamsProvider: React.FC<{ demo?: boolean }> = ({ children, demo }) => {
	const intl = useIntl()
	const navigate = useNavigate()
	const location = useLocation()
	const [debounceSearch, setDebounceSearch] = useState('')
	const [sort, setSort] = useState<string>('CREATED_AT')
	const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC')
	const teamId = location.pathname.split('/')[2]

	const [isExportMode, setIsExportMode] = useState(false)

	const allApplicantsRef = useRef<CoreApplicantItemFieldsFragment[]>([])
	const hasLoadedAllPages = useRef(false)

	const debouncedSearch = useCallback(
		debounce(nextValue => setDebounceSearch(nextValue), 300),
		[]
	)

	const [
		getAssessment,
		{ data: assessmentData, loading: assessmentLoading, refetch: refetchAssessment }
	] = useGetAssessmentLazyQuery({ errorPolicy: 'ignore' })

	const [
		getApplicants,
		{ data: applicantsData, loading: applicantsLoading, refetch: refetchApplicants }
	] = useGetApplicantsLazyQuery({
		nextFetchPolicy: 'cache-first',
		errorPolicy: 'ignore'
	})

	const [page, setPage] = useState(applicantsData?.applicants?.pageInfo.currentPage || 1)

	const handlePageChange = (_: React.ChangeEvent<unknown> | undefined, value: number) => {
		setPage(value)
		setIsExportMode(false)
	}

	const filters = useSearchFilter(
		{
			pipelineId: {
				label: intl.formatMessage({ id: 'talentGroup.filters.talentGroup' }),
				options: (assessmentData?.assessment.pipelines.map(pipeline => ({
					label: pipeline?.name || '',
					value: pipeline?.id || ''
				})) || []) as Option[]
			},
			isBookmarked: {
				label: intl.formatMessage({ id: 'talentGroup.filters.Bookmark' }),
				options: [
					{ label: 'Bookmark', value: true },
					{ label: 'Unbookmark', value: false }
				]
			},
			isShortlisted: {
				label: intl.formatMessage({ id: 'talentGroup.filters.Shortlist' }),
				options: [
					{ label: 'Shortlist', value: true },
					{ label: 'Unshortlist', value: false }
				]
			}
		},
		{ isToGraphql: true },
		[assessmentData?.assessment.pipelines]
	)

	const { checkoutFilter } = filters

	const handleSearch: React.ChangeEventHandler<
		HTMLTextAreaElement | HTMLInputElement
	> = e => {
		const { value: nextValue } = e.target
		debouncedSearch(nextValue)
		setIsExportMode(false)
	}

	const handleToggleOrder = () => {
		setOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'))
		setIsExportMode(false)
	}

	const handleChangeSort = event => {
		const value = event.target.value as string
		setSort(value)
		setIsExportMode(false)
	}

	const refetchApplicantsFn = async (
		type?: 'Internal' | 'External' | null,
		fetchAll: boolean = false
	): Promise<TeamContextProps['applicants'] | undefined> => {
		if (!refetchApplicants) return undefined

		const result = await refetchApplicants(
			removeUndefinedKeys({
				type,
				page,
				sort: `${sort}_${order}`.toUpperCase() as EnumSortApplicantGetManyInput,
				filter:
					// @ts-ignore
					debounceSearch || checkoutFilter.length > 0
						? {
								AND: [
									// @ts-ignore
									...checkoutFilter,
									debounceSearch ? { name: debounceSearch } : null
								].filter(Boolean)
						  }
						: null
			})
		)

		const applicants = result?.data?.applicants
		if (!applicants) return undefined

		if (fetchAll) {
			const existingIds = new Set(allApplicantsRef.current.map(a => a.id))
			const newItems = (applicants.items || []).filter(a => !existingIds.has(a.id))
			if (page === 1) {
				allApplicantsRef.current = applicants.items || []
			} else {
				allApplicantsRef.current = [...allApplicantsRef.current, ...newItems]
			}
		}

		return applicants
	}

	const client = useApolloClient()

	const [allApplicants, setAllApplicants] = useState<CoreApplicantItemFieldsFragment[]>([])
	const [loadingAllPages, setLoadingAllPages] = useState(false)
	const [hasFetchedAllPages, setHasFetchedAllPages] = useState(false)
	const fetchAllApplicants = async (): Promise<CoreApplicantItemFieldsFragment[]> => {
		if (
			!teamId ||
			!sort ||
			!applicantsData?.applicants?.pageInfo?.pageCount ||
			loadingAllPages
		) {
			return allApplicants
		}

		setLoadingAllPages(true)
		try {
			const pages = applicantsData.applicants.pageInfo.pageCount
			const currentPage = applicantsData.applicants.pageInfo.currentPage
			const combined = [...(applicantsData.applicants.items || [])]

			const fetchPromises: Promise<CoreApplicantItemFieldsFragment[]>[] = []

			for (let i = 1; i <= pages; i++) {
				if (i === currentPage) continue
				fetchPromises.push(
					client
						.query({
							query: GET_APPLICANTS,
							variables: {
								assessId: teamId,
								sort: `${sort}_${order}`.toUpperCase() as EnumSortApplicantGetManyInput,
								filter: null,
								page: i
							},
							fetchPolicy: 'network-only'
						})
						.then(res => {
							const items = res?.data?.applicants?.items || []
							// console.log(`Fetched ${items.length} applicants from page ${i}`)
							return items
						})

						.catch(err => {
							console.error(`Error fetching page ${i}:`, err)
							return []
						})
				)
			}

			const results = await Promise.all(fetchPromises)
			results.forEach(pageItems => combined.push(...pageItems))

			const uniqueApplicants = Array.from(
				new Map(combined.map(app => [app.id, app])).values()
			)

			setAllApplicants(uniqueApplicants)
			setHasFetchedAllPages(true)

			// console.log('All fetched applicants:', uniqueApplicants)
			return uniqueApplicants
		} catch (err) {
			console.error('fetchAllApplicants error:', err)
			return allApplicants
		} finally {
			setLoadingAllPages(false)
		}
	}
	useEffect(() => {
		fetchAllApplicants()
	}, [teamId, sort, applicantsData])

	useEffect(() => {
		if (!applicantsData?.applicants?.items || isExportMode) return

		if (page === 1) {
			allApplicantsRef.current = applicantsData.applicants.items
		} else {
			const existingIds = new Set(allApplicantsRef.current.map(a => a.id))
			const newItems = applicantsData.applicants.items.filter(a => !existingIds.has(a.id))
			allApplicantsRef.current = [...allApplicantsRef.current, ...newItems]
		}
	}, [applicantsData?.applicants?.items, page])

	return (
		<TeamsContext.Provider
			value={{
				debounceSearch,
				filters,
				teamId,
				page,
				demo: !!demo,
				handlePageChange,
				handleSearch,
				talents: isExportMode
					? allApplicantsRef.current
					: applicantsData?.applicants?.items || [],
				allApplicants,
				order,
				sort: `${sort}_${order}`.toUpperCase() as EnumSortApplicantGetManyInput,
				handleToggleOrder,
				handleChangeSort,
				assessment: assessmentData?.assessment,
				getAssessment,
				applicants: applicantsData?.applicants,
				applicantsLoading,
				getApplicants,
				assessmentLoading,
				refetchAssessment: async () => {
					if (refetchAssessment) await refetchAssessment()
				},
				refetchApplicants: refetchApplicantsFn,

				fetchAllApplicants,
				resetExportMode: () => setIsExportMode(false)
			}}
		>
			{children}
		</TeamsContext.Provider>
	)
}

export default TeamsProvider
